/**
 * LimitReachedDialog — modal global que aparece cuando el backend devuelve
 * 403 con header `X-Limit-Type`. El interceptor de `lib/api.js` despacha el
 * evento `dealider:limit-reached` con todos los datos.
 *
 * Tipos soportados:
 *  - users               → "Contacta a ventas para aumentar usuarios"
 *  - contacts_monthly    → "Llegaste al tope de leads mensuales"
 *  - developments        → "Agotaste tus desarrollos del plan"
 *  - mass_campaign_sends → "Cupo mensual de envíos alcanzado"
 *  - contacts            → fallback histórico (legacy)
 */
import { useEffect, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowUpRight, Phone, X } from 'lucide-react';

const LIMIT_COPY = {
  users: {
    title: 'Has alcanzado el límite de usuarios de tu plan',
    desc: 'Tu plan actual permite registrar un número limitado de usuarios. Para sumar más miembros al equipo, contacta a ventas ' + APP_NAME + ' y cambiamos tu plan al instante.',
    cta: 'Contactar a ventas',
  },
  contacts_monthly: {
    title: 'Llegaste al límite mensual de leads',
    desc: 'Tu plan tiene un cupo máximo de leads por mes. Llegaste al tope. Tus leads históricos siguen intactos y puedes seguir trabajándolos, pero para captar más este mes, necesitas un upgrade.',
    cta: 'Hablar con ventas para upgrade',
  },
  contacts: {
    title: 'Llegaste al límite de leads de tu plan',
    desc: 'Tu plan tiene un cupo de captación de leads que se ha alcanzado. Contacta a ventas ' + APP_NAME + ' para ampliar tu plan al instante.',
    cta: 'Hablar con ventas para upgrade',
  },
  developments: {
    title: 'Llegaste al límite de desarrollos de tu plan',
    desc: 'Tu plan inmobiliario tiene un cupo máximo de desarrollos. Para sumar un nuevo proyecto, contacta a ventas ' + APP_NAME + ' y ampliamos tu plan.',
    cta: 'Contactar a ventas',
  },
  mass_campaign_sends: {
    title: 'Cupo mensual de envíos masivos alcanzado',
    desc: 'Tu plan permite un número limitado de envíos masivos por mes. Para enviar más campañas este mes, necesitas upgradear tu plan.',
    cta: 'Hablar con ventas para upgrade',
  },
  mass_campaign_leads: {
    title: 'Cupo de leads para campañas alcanzado',
    desc: 'Las campañas masivas de tu plan tienen un número máximo de leads por mes. Llegaste al tope.',
    cta: 'Hablar con ventas',
  },
};

export default function LimitReachedDialog() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const handler = (event) => {
      setInfo(event.detail || null);
    };
    window.addEventListener('dealider:limit-reached', handler);
    return () => window.removeEventListener('dealider:limit-reached', handler);
  }, []);

  if (!info) return null;
  const copy = LIMIT_COPY[info.limit_type] || {
    title: 'Has alcanzado un límite de tu plan',
    desc: info.detail || 'Tu plan actual ha alcanzado uno de sus límites. Contacta a ventas ' + APP_NAME + ' para upgradear.',
    cta: 'Contactar a ventas',
  };
  const pct = info.max > 0 ? Math.min(100, Math.round((info.current / info.max) * 100)) : 100;

  const handleClose = () => setInfo(null);
  const openSalesContact = () => {
    handleClose();
    // Abrir WhatsApp con mensaje pre-cargado o mailto. Reemplazable por modal en el futuro.
    const subject = encodeURIComponent(`Solicitud de upgrade de plan — ${info.limit_type}`);
    const body = encodeURIComponent(
      `Hola, llegué al límite de mi plan ${APP_NAME} (${info.limit_type}: ${info.current}/${info.max}). ` +
      'Quiero hablar con un ejecutivo para upgradear.'
    );
    window.location.href = `mailto:ventas@dealider.com?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={!!info} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md bg-card border-amber-500/30" data-testid="limit-reached-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
          </div>
          <DialogTitle className="text-white" data-testid="limit-reached-title">{copy.title}</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {copy.desc}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="limit-reached-meter">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Uso actual</span>
            <span className="text-white font-bold">
              {info.current} <span className="text-muted-foreground">/ {info.max}</span>
              {info.period === 'month' && <span className="text-amber-400 ml-1">/mes</span>}
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Button onClick={openSalesContact} className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-bold" data-testid="limit-reached-cta">
            <Phone className="w-4 h-4 mr-2" /> {copy.cta} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <Button onClick={handleClose} variant="outline" className="border-white/10 text-muted-foreground" data-testid="limit-reached-close">
            <X className="w-4 h-4 mr-1" /> Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
